// front/src/i18n/I18nProvider.jsx
// ✅ Fix: applyHtmlDirection before paint (useLayoutEffect) to prevent RTL/LTR flicker/mismatch
// ✅ Fix: avoid stale `lang` in startup profile override (langRef)

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { loadTranslations } from "./loadTranslations";
import { setGlobalT } from "./i18nBridge";

// ✅ Use the project's axios instance (handles baseURL + auth)
import api from "../api/Api";

const I18nContext = createContext({
  lang: "fa",
  language: "fa",
  dir: "rtl",
  isRtl: true,
  setLang: () => {},
  setLanguage: () => {},
  t: (key) => key,
  ready: false,
  reload: async () => {},
});

const STORAGE_KEY = "lang";
const DEFAULT_LANG = "fa";
const SUPPORTED = new Set(["fa", "en"]);

/** ---------- helpers ---------- */

function getBaseLang(value) {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return "";
  return v.split("-")[0];
}

function normalizeLang(value) {
  const base = getBaseLang(value);
  return SUPPORTED.has(base) ? base : DEFAULT_LANG;
}

function readLangFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get("lang");
    return lang ? normalizeLang(lang) : null;
  } catch {
    return null;
  }
}

function readLangFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? normalizeLang(stored) : null;
  } catch {
    return null;
  }
}

function writeLangToStorage(lang) {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // ignore
  }
}

function applyHtmlDirection(lang) {
  const normalized = normalizeLang(lang);
  const isRtl = normalized === "fa";
  const dir = isRtl ? "rtl" : "ltr";

  try {
    document.documentElement.setAttribute("lang", normalized);
    document.documentElement.setAttribute("dir", dir);

    document.body.classList.remove("rtl", "ltr");
    document.body.classList.add(dir);
  } catch {
    // ignore
  }
}

/**
 * ✅ Non-blocking sync: PATCH profile.language
 */
async function syncProfileLanguage(lang) {
  const language = normalizeLang(lang);

  try {
    await api.patch("/v1/account/profile/", { language });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      "syncProfileLanguage failed:",
      e?.response?.status,
      e?.response?.data || e?.message
    );
  }
}

/** ---------- Provider ---------- */

export function I18nProvider({ children }) {
  const initialLang = useMemo(() => {
    return readLangFromUrl() || readLangFromStorage() || DEFAULT_LANG;
  }, []);

  const [lang, setLangState] = useState(initialLang);
  const [translations, setTranslations] = useState({});
  const [ready, setReady] = useState(false);

  const lastLoadedRef = useRef({ lang: null, version: 0 });

  // ✅ debounce backend sync (avoid spamming)
  const syncTimerRef = useRef(null);

  // ✅ keep latest lang for startup async effect (avoid stale closure)
  const langRef = useRef(lang);
  useEffect(() => {
    langRef.current = lang;
  }, [lang]);

  /**
   * ✅ INTERNAL setter: can skip backend sync
   * - used for startup override from profile.language
   */
  const setLangInternal = useCallback((nextLang, { syncBackend = true } = {}) => {
    const normalized = normalizeLang(nextLang);

    // 1) UI: immediate
    setLangState(normalized);
    writeLangToStorage(normalized);
    applyHtmlDirection(normalized);

    // 2) Backend: debounced sync (optional)
    if (!syncBackend) return;

    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      syncProfileLanguage(normalized);
    }, 250);
  }, []);

  // ✅ public setter (always sync backend)
  const setLang = useCallback(
    (nextLang) => {
      setLangInternal(nextLang, { syncBackend: true });
    },
    [setLangInternal]
  );

  const reload = useCallback(
    async (forcedLang) => {
      const targetLang = normalizeLang(forcedLang || lang);

      setReady(false);
      try {
        const payload = await loadTranslations(targetLang);

        const dict =
          payload?.translations && typeof payload.translations === "object"
            ? payload.translations
            : {};

        setTranslations(dict);

        lastLoadedRef.current = {
          lang: targetLang,
          version: (lastLoadedRef.current.version || 0) + 1,
        };
      } catch {
        setTranslations({});
      } finally {
        setReady(true);
      }
    },
    [lang]
  );

  // ✅ IMPORTANT: set html/body dir BEFORE paint (prevents RTL/LTR flicker)
  useLayoutEffect(() => {
    applyHtmlDirection(lang);
  }, [lang]);

  // ✅ translations load can stay in useEffect (async / non-blocking)
  useEffect(() => {
    if (lastLoadedRef.current.lang !== lang) {
      reload(lang);
    }
  }, [lang, reload]);

  /**
   * ✅ Startup: if user is logged-in and URL has no ?lang,
   * read profile.language and override UI (WITHOUT PATCH)
   */
  useEffect(() => {
    const urlLang = readLangFromUrl();
    if (urlLang) return; // ✅ URL wins

    let cancelled = false;

    (async () => {
      try {
        const res = await api.get("/v1/account/profile/");
        const profileLang = normalizeLang(res?.data?.language);

        // اگر پروفایل language معتبر بود و با current فرق داشت، override کن
        if (!cancelled && profileLang && profileLang !== langRef.current) {
          setLangInternal(profileLang, { syncBackend: false }); // ✅ no PATCH loop
        }
      } catch (e) {
        // اگر auth نبود یا 401، طبیعی است (public pages)
      }
    })();

    return () => {
      cancelled = true;
    };
    // فقط یکبار در startup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useCallback(
    (key, fallback) => {
      const k = String(key || "").trim();
      if (!k) return "";

      const v = translations?.[k];
      if (typeof v === "string" && v.length > 0) return v;

      if (typeof fallback === "string" && fallback.length > 0) return fallback;

      return k;
    },
    [translations]
  );

  useEffect(() => {
    if (ready) setGlobalT(t);
  }, [ready, t]);

  const isRtl = normalizeLang(lang) === "fa";
  const dir = isRtl ? "rtl" : "ltr";

  const value = useMemo(
    () => ({
      lang,
      language: lang,
      dir,
      isRtl,
      setLang,
      setLanguage: setLang,
      t,
      ready,
      reload: () => reload(lang),
    }),
    [lang, dir, isRtl, setLang, t, ready, reload]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
